import { Chip, Menu } from "@mui/material";
import { useMemo, useRef, useState } from "react";
import { exportCSVText } from "constants/text";
import Download from "@mui/icons-material/Download";
import LargePDFModal from "./LargePDFModal";
import { AcknowledgmentEmailFormatType } from "types/declarations/enums";
import { ExportFileMenuItem } from "components/ExportFile/ExportButtons.styles.ts";
import ExportButtonsMenuItems from "./ExportButtonsMenuItems.tsx";

interface ExportButtonsProps {
	largePDF: boolean;
	buttonHeader: string;
	generatePDF?: (format?: AcknowledgmentEmailFormatType) => void;
	generateExcel?: () => void;
	isOnlyPDF?: boolean;
	isOnlyExcel?: boolean;
	isAcknowledgements?: boolean;
	className?: string;
	isOrderDetailsStyled?: boolean;
	isMerchOrder?: boolean;
}

const ExportButtons = ({
	largePDF,
	buttonHeader,
	generatePDF,
	generateExcel,
	isOnlyPDF = false,
	isOnlyExcel = false,
	isAcknowledgements = false,
	className,
	isOrderDetailsStyled = false,
	isMerchOrder = false
}: ExportButtonsProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const anchorRef = useRef(null);
	const [showExportPDFTooLarge, setShowExportPDFTooLarge] = useState(false);

	const resolvedAction = useMemo(() => {
		if (isOnlyPDF && generatePDF) return generatePDF;
		if (isOnlyExcel && generateExcel) return generateExcel;
		return () => setIsOpen((prev) => !prev);
	}, [isOnlyPDF, generatePDF, isOnlyExcel, generateExcel]);

	const handleMenuToggle = async () => {
		resolvedAction();
	};

	const pdfClose = () => {
		setShowExportPDFTooLarge((prevState) => !prevState);
	};

	const pdfOptionSelected = (format?: AcknowledgmentEmailFormatType) => {
		setIsOpen(false);

		if (largePDF) {
			setShowExportPDFTooLarge((prevState) => !prevState);
		} else {
			generatePDF?.(format);
		}
	};

	const excelOptionSelected = () => {
		setIsOpen(false);

		if (showExportPDFTooLarge) {
			pdfClose();
		}

		if (generateExcel) {
			generateExcel();
		}
	};

	const iconColor = isOrderDetailsStyled ? "var(--cwg-blue-900)" : "var(--text-secondary)";
	const chipLabelColor = isOrderDetailsStyled ? "var(--cwg-blue-900)" : "var(--text-secondary)";
	const chipIcon = (
		<Download
			style={{
				color: iconColor,
				width: "18px",
				margin: 0
			}}
		/>
	);

	return (
		<div>
			<Chip
				ref={anchorRef}
				className={className}
				clickable
				onClick={handleMenuToggle}
				sx={{
					gap: isOrderDetailsStyled ? "8px" : undefined,
					"& .MuiChip-label": {
						fontSize: "13px",
						fontFamily: "canada-type-gibson",
						fontWeight: "var(--font-weight-medium)",
						letterSpacing: "var(--letter-spacing-10)",
						padding: 0,
						textAlign: "center",
						lineHeight: "1.5rem",
						marginRight: isOrderDetailsStyled ? 0 : ".5rem",
						marginTop: "1px",
						overflow: isOrderDetailsStyled ? "visible" : undefined,
						textOverflow: isOrderDetailsStyled ? "clip" : undefined,
						maxWidth: isOrderDetailsStyled ? "none" : undefined
					},
					color: chipLabelColor,
					height: "30px",
					background: "none",
					borderRadius: "0px",
					padding: isOrderDetailsStyled ? "0 2px" : "4px 5px",
					backgroundColor: isOpen ? "var(--export-button)" : "none",
					...(isOrderDetailsStyled && {
						"& .MuiChip-icon": {
							marginLeft: "0 !important",
							marginRight: "0 !important"
						}
					})
				}}
				label={buttonHeader}
				onDelete={isOrderDetailsStyled ? undefined : handleMenuToggle}
				deleteIcon={isOrderDetailsStyled ? undefined : chipIcon}
				icon={isOrderDetailsStyled ? chipIcon : undefined}
				data-testid="order-list-export-orders"
			/>
			<Menu
				open={isOpen}
				anchorEl={anchorRef.current}
				onClose={handleMenuToggle}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right"
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right"
				}}
				data-test-id="export-menu"
			>
				<ExportButtonsMenuItems
					isAcknowledgements={isAcknowledgements}
					isMerchOrder={isMerchOrder}
					onPdfSelect={pdfOptionSelected}
				/>
				<ExportFileMenuItem
					onClick={excelOptionSelected}
					data-testid="order-list-export-excel"
				>
					{exportCSVText}
				</ExportFileMenuItem>
			</Menu>
			<LargePDFModal
				handleClose={pdfClose}
				open={showExportPDFTooLarge}
				popupAnchor={anchorRef.current}
				onExportAsExcel={excelOptionSelected}
			/>
		</div>
	);
};

export default ExportButtons;
